import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarCheck, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../api";
import CursoTabsNav from "../components/CursoTabsNav";

/**
 * Quadro de Presenças — matriz editável (formandos × sessões).
 *
 * As presenças vivem em `curso.Trel_CheckInSessao` (mesma tabela que o
 * QR de check-in escreve). Aqui o admin pode corrigir manualmente:
 * clicar num cell alterna presente/não-presente.
 */
export default function AdminCursoPresencas() {
  const [params] = useSearchParams();
  const cursoId = params.get("curso") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState({}); // { "fichaId:ordem": true }

  const load = useCallback(async () => {
    if (!cursoId) return;
    setLoading(true); setError("");
    try {
      const r = await adminApi.cursoPresencasMatrix(cursoId);
      setData(r);
    } catch (e) {
      setError(e.message || "Erro a carregar quadro de presenças");
    } finally { setLoading(false); }
  }, [cursoId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (fid, ordem, currentPresente) => {
    const key = `${fid}:${ordem}`;
    if (saving[key]) return;
    setSaving((s) => ({ ...s, [key]: true }));
    // Optimistic UI
    const next = !currentPresente;
    setData((d) => {
      if (!d) return d;
      return {
        ...d,
        formandos: d.formandos.map((f) => {
          if (f.ID_FichaInscricao !== fid) return f;
          const p = { ...(f.presencas || {}) };
          if (next) p[String(ordem)] = { presente: true, por: "admin", dataCheckIn: new Date().toISOString() };
          else delete p[String(ordem)];
          return { ...f, presencas: p };
        }),
      };
    });
    try {
      await adminApi.cursoPresencaToggle(cursoId, fid, ordem, next);
    } catch (e) {
      toast.error(e.message || "Erro a alterar presença");
      load(); // reverter
    } finally { setSaving((s) => { const c = { ...s }; delete c[key]; return c; }); }
  };

  const stats = useMemo(() => {
    if (!data) return null;
    const total = data.formandos.length * data.sessoes.length;
    let marked = 0;
    data.formandos.forEach((f) => {
      Object.values(f.presencas || {}).forEach((p) => { if (p.presente) marked += 1; });
    });
    return { total, marked, pct: total ? Math.round((marked * 100) / total) : 0 };
  }, [data]);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
    } catch { return iso; }
  };

  return (
    <div className="p-6 max-w-full" data-testid="presencas-page">
      <div className="flex items-center gap-3 mb-4">
        <CalendarCheck className="w-6 h-6 text-[#A8843C]" />
        <div>
          <h1 className="text-xl font-semibold text-[#1B1B1B]">Quadro de Presenças</h1>
          <p className="text-xs text-[#978D78]">Curso #{cursoId}. Clica num cell para alternar presença/ausência.</p>
        </div>
        <button
          type="button"
          onClick={load}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded bg-[#F3F0E7] text-[#0F1829] hover:bg-[#E6E1D6]"
          data-testid="presencas-reload"
        >
          <RefreshCw className="w-3.5 h-3.5"/> Recarregar
        </button>
      </div>

      <CursoTabsNav cursoId={cursoId} />

      {loading && (
        <div className="mt-6 flex items-center gap-2 text-sm text-[#978D78]">
          <Loader2 className="w-4 h-4 animate-spin"/> A carregar...
        </div>
      )}
      {error && <div className="mt-6 text-sm text-red-600" data-testid="presencas-error">{error}</div>}

      {data && (
        <>
          {stats && (
            <div className="mt-4 mb-3 text-xs text-[#6B7280]">
              <b>{data.formandos.length}</b> formandos · <b>{data.sessoes.length}</b> sessões · presenças marcadas: <b>{stats.marked}/{stats.total}</b> ({stats.pct}%)
            </div>
          )}

          {data.sessoes.length === 0 ? (
            <div className="mt-4 p-4 bg-[#FEF7E0] border border-[#F4E4A8] rounded text-sm text-[#7A5D00]">
              Este curso ainda não tem <b>plano de sessões</b> definido em <code>Trel_PlanoSessao</code>.
              Cria o plano de sessões primeiro para poderes marcar presenças.
            </div>
          ) : data.formandos.length === 0 ? (
            <div className="mt-4 p-4 bg-[#FEF7E0] border border-[#F4E4A8] rounded text-sm text-[#7A5D00]">
              Este curso não tem formandos activos inscritos.
            </div>
          ) : (
            <div className="overflow-x-auto border border-[#E6E1D6] rounded bg-white" data-testid="presencas-grid">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="bg-[#0F1829] text-white">
                    <th className="text-left p-2 sticky left-0 bg-[#0F1829] z-10 min-w-[220px]">Formando</th>
                    {data.sessoes.map((s) => (
                      <th key={s.Ordem} className="p-1 text-center min-w-[60px]" title={s.HoraSessao || s.HorarioManha}>
                        <div className="font-bold">S{s.Ordem}</div>
                        <div className="text-[10px] font-normal text-[#D4C8A8]">{fmtDate(s.Data)}</div>
                      </th>
                    ))}
                    <th className="p-2 text-center min-w-[60px] bg-[#A8843C]">%</th>
                  </tr>
                </thead>
                <tbody>
                  {data.formandos.map((f, i) => {
                    const present = Object.values(f.presencas || {}).filter((p) => p.presente).length;
                    const pct = data.sessoes.length ? Math.round((present * 100) / data.sessoes.length) : 0;
                    return (
                      <tr key={f.ID_FichaInscricao} className={i % 2 === 0 ? "bg-white" : "bg-[#FAF8F2]"}>
                        <td className="p-2 sticky left-0 bg-inherit font-medium border-r border-[#E6E1D6] z-[1]" title={f.Email}>
                          {f.NomeCompleto}
                        </td>
                        {data.sessoes.map((s) => {
                          const p = f.presencas?.[String(s.Ordem)];
                          const isPresent = !!p?.presente;
                          const key = `${f.ID_FichaInscricao}:${s.Ordem}`;
                          const isSaving = !!saving[key];
                          return (
                            <td key={s.Ordem} className="p-0.5 text-center">
                              <button
                                type="button"
                                onClick={() => toggle(f.ID_FichaInscricao, s.Ordem, isPresent)}
                                disabled={isSaving}
                                title={p ? `Presente em ${fmtDate(p.dataCheckIn)} por ${p.por || "?"}` : "Ausente — click para marcar presente"}
                                className={`w-9 h-8 rounded font-bold text-sm transition ${
                                  isPresent
                                    ? "bg-[#2F7D32] text-white hover:bg-[#256428]"
                                    : "bg-[#F3F0E7] text-[#B94A48] hover:bg-[#E6E1D6]"
                                } ${isSaving ? "opacity-50" : ""}`}
                                data-testid={`presenca-cell-${f.ID_FichaInscricao}-${s.Ordem}`}
                              >
                                {isSaving ? "…" : isPresent ? "P" : "F"}
                              </button>
                            </td>
                          );
                        })}
                        <td className="p-2 text-center font-bold bg-[#FAF3E4] border-l border-[#E6E1D6]">
                          <span className={pct >= 90 ? "text-[#2F7D32]" : pct >= 50 ? "text-[#A8843C]" : "text-[#B94A48]"}>
                            {pct}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-3 text-[11px] text-[#978D78]">
            Legenda: <span className="inline-block px-1.5 py-0.5 bg-[#2F7D32] text-white rounded font-bold">P</span> Presente
            &nbsp;·&nbsp;
            <span className="inline-block px-1.5 py-0.5 bg-[#F3F0E7] text-[#B94A48] rounded font-bold">F</span> Faltou
          </div>
        </>
      )}
    </div>
  );
}
